import { cleanRichTextValue } from "app/shared/ui/RichTextEditor";
import { ReportTextReferenceDataItem } from "app/shared/api/getReportTextReferenceData";
import {
  type DivisionReportTextDto,
  normalizeDivisionReportTextDto,
} from "app/shared/api/getDivision";
export type { DivisionReportTextDto };
export const BOOKING_DOCUMENT_SENDING_TEMPLATE_NAME = "Booking Document Sending Template";
export const QUOTE_CONFIRMATION_EMAIL_NAME = "Quote Confirmation Email";
export const RESERVATION_CONFIRMATION_EMAIL_NAME = "Reservation Confirmation Email";
export const REPORT_TEXT_RECIPIENT_STUDENTS = 1;
export const REPORT_TEXT_RECIPIENT_AGENTS = 2;
export const reportTextTemplateSupportsForWhom = (
  item: ReportTextReferenceDataItem
): boolean => {
  if (item.supportsForWhom === true) {
    return true;
  }
  const used = String(item.usedIn ?? "").trim().toLowerCase();
  if (
    used.includes("quote stage") ||
    used.includes("reservation stage") ||
    used.includes("booking stage")
  ) {
    return true;
  }
  const name = String(item.name ?? "").trim().toLowerCase();
  if (
    name === BOOKING_DOCUMENT_SENDING_TEMPLATE_NAME.toLowerCase() ||
    name.includes("quote document sending") ||
    name.includes("reservation document sending") ||
    name.includes("booking document sending")
  ) {
    return true;
  }
  return false;
};

export type ReportTextTemplate = {
  reportTextId: number;
  referenceId: string;
  name: string;
  usedIn: string;
  supportsRecipientType: boolean;
};

export const reportTextTemplateRestrictsStudentRecipient = (
  template: Pick<ReportTextTemplate, "name">
): boolean => {
  const n = String(template.name ?? "").trim().toLowerCase();
  return (
    n === QUOTE_CONFIRMATION_EMAIL_NAME.toLowerCase() ||
    n === RESERVATION_CONFIRMATION_EMAIL_NAME.toLowerCase()
  );
};

export type ReportTextFormEntry = {
  content?: string;
  studentContent?: string;
  agentContent?: string;
};

export type ReportTextFormState = Record<string, ReportTextFormEntry>;

export const reportTextRowKey = (reportTextId: number, forWhomId?: number | null): string => {
  if (forWhomId === undefined || forWhomId === null) {
    return String(reportTextId);
  }
  return `${reportTextId}:${forWhomId}`;
};

type MapDivisionToFormParams = {
  templates: ReportTextTemplate[];
  divisionReportTexts?: DivisionReportTextDto[] | null;
};

type BuildPayloadParams = {
  templates: ReportTextTemplate[];
  reportTextById?: ReportTextFormState;
  existingIdsByReportTextRow?: Record<string, number>;
  divisionId: number | null;
  centreId: number | null;
  format?: number;
};

export const buildReportTextTemplates = (
  referenceData: ReportTextReferenceDataItem[]
): ReportTextTemplate[] => {
  const unique = new Map<number, ReportTextTemplate>();

  referenceData.forEach((item) => {
    if (!Number.isFinite(item.reportTextId)) {
      return;
    }

    if (!unique.has(item.reportTextId)) {
      unique.set(item.reportTextId, {
        reportTextId: item.reportTextId,
        referenceId: String(item.id),
        name: String(item.name ?? ""),
        usedIn: String(item.usedIn ?? ""),
        supportsRecipientType: reportTextTemplateSupportsForWhom(item),
      });
    }
  });

  return Array.from(unique.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const buildEmptyReportTextFormState = (
  templates: ReportTextTemplate[]
): ReportTextFormState => {
  return templates.reduce<ReportTextFormState>((acc, template) => {
    acc[String(template.reportTextId)] = template.supportsRecipientType
      ? { studentContent: "", agentContent: "" }
      : { content: "" };
    return acc;
  }, {});
};

export const mapDivisionReportTextsToFormState = ({
  templates,
  divisionReportTexts,
}: MapDivisionToFormParams): {
  reportTextById: ReportTextFormState;
  existingIdsByReportTextRow: Record<string, number>;
} => {
  const templateById = new Map<number, ReportTextTemplate>(
    templates.map((t) => [t.reportTextId, t])
  );
  const reportTextById = buildEmptyReportTextFormState(templates);
  const existingIdsByReportTextRow: Record<string, number> = {};

  if (!Array.isArray(divisionReportTexts)) {
    return { reportTextById, existingIdsByReportTextRow };
  }

  divisionReportTexts.forEach((raw) => {
    const item = normalizeDivisionReportTextDto(raw);
    if (!item || item.isDeleted) {
      return;
    }
    const reportTextId = item.reportTextId;
    const template = templateById.get(reportTextId);
    if (!template) {
      return;
    }

    const content = cleanRichTextValue(item.content) ?? "";
    const whom = item.forWhomId;
    const key = reportTextRowKey(
      reportTextId,
      template.supportsRecipientType ? whom ?? REPORT_TEXT_RECIPIENT_STUDENTS : undefined
    );

    if (template.supportsRecipientType) {
      const effectiveWhom =
        whom === REPORT_TEXT_RECIPIENT_AGENTS
          ? REPORT_TEXT_RECIPIENT_AGENTS
          : REPORT_TEXT_RECIPIENT_STUDENTS;
      const entry = reportTextById[String(reportTextId)] ?? {
        studentContent: "",
        agentContent: "",
      };
      if (effectiveWhom === REPORT_TEXT_RECIPIENT_AGENTS) {
        entry.agentContent = content;
      } else {
        entry.studentContent = content;
      }
      reportTextById[String(reportTextId)] = entry;
    } else {
      reportTextById[String(reportTextId)] = { content };
    }

    if (typeof item.id === "number" && item.id > 0) {
      existingIdsByReportTextRow[key] = item.id;
    }
  });

  return { reportTextById, existingIdsByReportTextRow };
};

export const buildDivisionReportTextsPayload = ({
  templates,
  reportTextById,
  existingIdsByReportTextRow,
  divisionId,
  centreId,
  format = 1,
}: BuildPayloadParams): DivisionReportTextDto[] => {
  const result: DivisionReportTextDto[] = [];
  const usedPayloadIds = new Set<number>();

  const assignExistingId = (
    mapped: DivisionReportTextDto,
    existingId?: number
  ): void => {
    if (typeof existingId !== "number" || existingId <= 0) {
      return;
    }
    if (usedPayloadIds.has(existingId)) {
      return;
    }
    mapped.id = existingId;
    usedPayloadIds.add(existingId);
  };

  templates.forEach((template) => {
    const key = String(template.reportTextId);
    const formEntry = reportTextById?.[key];

    if (template.supportsRecipientType) {
      const studentRaw = formEntry?.studentContent;
      const agentRaw = formEntry?.agentContent;
      const studentContent = cleanRichTextValue(studentRaw) ?? "";
      const agentContent = cleanRichTextValue(agentRaw) ?? "";
      const studentId = existingIdsByReportTextRow?.[
        reportTextRowKey(template.reportTextId, REPORT_TEXT_RECIPIENT_STUDENTS)
      ];
      const agentId = existingIdsByReportTextRow?.[
        reportTextRowKey(template.reportTextId, REPORT_TEXT_RECIPIENT_AGENTS)
      ];

      const pushRow = (
        content: string,
        forWhomId: number,
        existingId?: number
      ) => {
        if (!content && !(typeof existingId === "number" && existingId > 0)) {
          return;
        }
        const mapped: DivisionReportTextDto = {
          reportTextId: template.reportTextId,
          divisionId,
          centreId,
          content,
          format,
          isDeleted: false,
          forWhomId,
        };
        assignExistingId(mapped, existingId);
        result.push(mapped);
      };

      pushRow(studentContent, REPORT_TEXT_RECIPIENT_STUDENTS, studentId);
      pushRow(agentContent, REPORT_TEXT_RECIPIENT_AGENTS, agentId);
      return;
    }

    const rawContent = formEntry?.content;
    const content = cleanRichTextValue(rawContent) ?? "";
    const existingId = existingIdsByReportTextRow?.[reportTextRowKey(template.reportTextId)];

    if (!content && !(typeof existingId === "number" && existingId > 0)) {
      return;
    }

    const mapped: DivisionReportTextDto = {
      reportTextId: template.reportTextId,
      divisionId,
      centreId,
      content,
      format,
      isDeleted: false,
    };

    assignExistingId(mapped, existingId);

    result.push(mapped);
  });

  return result;
};
